import { PlanTrailActionContext } from "./types";
import { PlanTrailState } from "../PlanTrailModule";
import { Route, RouteFactory } from "../../../../../utils/models/Route";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { ApplyDayCountCommand } from "../../undo/commands/ApplyDayCountCommand";
import {
  canDeriveMasterTrail,
  deriveMasterTrail,
  sliceMasterTrailIntoDays,
  cumulativeDistances,
  nearestOnTrail,
  routeAnchorCoord,
  dayNumberForDistance,
  MAX_GUIDED_DAYS,
  MIN_GUIDED_DAYS,
  LockedBoundary,
} from "../../../utils/masterTrail";
import { deriveBoundariesFromRoutes } from "../../../services/locked-boundaries";

const isMainRouteStyle = (route: Route): boolean => !route.route_style || route.route_style === 'route';

function buildBoundaryMarkers(coordinates: number[][]): MarkerFeature[] {
  const start = coordinates[0];
  const end = coordinates[coordinates.length - 1];
  return [start, end].map(coord => new MarkerFeature(
    new MarkerGeometry([coord[0], coord[1], coord[2] ?? 0]),
    new MarkerProperties(generateID(), true, Date.now().toString(), coord[2] ?? 0)
  ));
}

/**
 * Freeze the master trail into the slicing source every locked-mode boundary
 * drag uses forever after — derived once from current main-line routes, then
 * never re-derived (a day's own route may later contain a baked-in detour
 * from a drag, which would corrupt a fresh re-derivation).
 */
function freezeTrail(routes: Route[]): { coordinates: number[][]; cumDist: number[] } | null {
  const trail = deriveMasterTrail(routes);
  if (trail.coordinates.length < 2) return null;
  return { coordinates: trail.coordinates, cumDist: cumulativeDistances(trail.coordinates) };
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Freezing the trail right after the last segment was drawn/dragged is a
 * race: `createPolylineGeoJson` (polylines.ts) returns a straight-line
 * PROVISIONAL segment immediately and replaces it with the real routed
 * geometry once GraphHopper responds (tracked in
 * `state.pendingProvisionalReplacements`). Freezing before that replacement
 * lands bakes the straight line into the trail forever. Wait briefly for any
 * in-flight replacements to settle before freezing; give up after
 * `timeoutMs` rather than block the click indefinitely.
 */
export async function waitForPendingRouting(state: PlanTrailState, timeoutMs: number = 8000): Promise<boolean> {
  const start = Date.now();
  while (state.pendingProvisionalReplacements.size > 0) {
    if (Date.now() - start > timeoutMs) return false;
    await sleep(100);
  }
  return true;
}

export const guidedRouteActions = {
  /**
   * Locks the plan to its existing main-line geometry, concatenated into one
   * frozen master trail. This must NOT redraw or re-split any existing
   * route — every day/alt/spur/transport route stays exactly as it is; only
   * the (invisible) frozen trail + the day boundaries derived from the
   * CURRENT split points are established. Re-splitting into even days only
   * ever happens from an explicit day-count change (the stepper/pace
   * wizard → applyDayCount). No-ops with a warning if the current routes
   * don't form a single continuous line (branches/loops).
   */
  async enableGuidedMode({ state, getters, dispatch }: PlanTrailActionContext): Promise<boolean> {
    let routes = getters['routesArray'] as Route[];
    if (!canDeriveMasterTrail(routes)) {
      console.warn('[GUIDED_MODE] Cannot enable — route has no continuous main line to lock to');
      return false;
    }

    if (state.pendingProvisionalReplacements.size > 0) {
      const settled = await waitForPendingRouting(state);
      if (!settled) {
        console.warn('[GUIDED_MODE] Cannot enable — still waiting for the last route calculation to finish');
        return false;
      }
      // Route objects were mutated in place by the settled replacement(s),
      // but re-read in case routes were added/removed while waiting.
      routes = getters['routesArray'] as Route[];
    }

    const frozen = state.lockedMasterTrail ?? freezeTrail(routes);
    state.lockedMasterTrail = frozen;
    if (frozen) {
      state.lockedBoundaries = deriveBoundariesFromRoutes(routes, frozen);
    }

    state.guidedDayCount = Math.max(getters['maxDay'] as number, MIN_GUIDED_DAYS);
    state.routeLocked = true;

    // Nothing about the routes changed — just refresh the debug overlay
    // (now eligible to draw the frozen trail instead of a live re-derivation).
    await dispatch('updateMasterTrailDebugLayer');
    return true;
  },

  /**
   * Switches off guided mode. Every existing free-edit action already works
   * unconditionally, so this only flips which UI is shown — no data changes.
   * The frozen trail/boundaries are cleared so a future re-lock re-baselines
   * against whatever free-edit state exists at that moment.
   */
  async disableGuidedMode({ state, dispatch }: PlanTrailActionContext) {
    state.routeLocked = false;
    state.lockedMasterTrail = null;
    state.lockedBoundaries = {};

    // The reference-trail overlay only has meaning while locked. Turning the
    // toggle back off (not just re-dispatching the layer refresh) matters:
    // updateMasterTrailDebugLayer falls back to a live re-derivation from
    // the current routes when not locked (a QA-only preview capability), so
    // leaving the flag on would redraw the overlay from that instead of
    // hiding it.
    state.debugMasterTrailVisible = false;
    await dispatch('updateMasterTrailDebugLayer');
  },

  /**
   * Re-baselines the frozen trail + boundaries from whatever main-line routes
   * exist right now. Used after a structural change made WHILE LOCKED that
   * legitimately shortens/changes the trail itself (currently: deleting a
   * whole day) — unlike a boundary drag, which always preserves the old
   * frozen trail exactly and only ever adds a detour. No-ops (leaving the
   * existing frozen trail/boundaries untouched) when not locked, or when
   * the remaining routes no longer form a single continuous line (in which
   * case there's nothing valid to freeze — locked mode is left in place but
   * boundary drags will no-op until the user re-locks).
   */
  async refreezeLockedTrail({ state, getters, dispatch }: PlanTrailActionContext): Promise<boolean> {
    if (!state.routeLocked) return false;

    const routes = getters['routesArray'] as Route[];
    if (!canDeriveMasterTrail(routes)) {
      console.warn('[GUIDED_MODE] Cannot refreeze — remaining routes do not form a single continuous line');
      state.lockedMasterTrail = null;
      state.lockedBoundaries = {};
      await dispatch('updateMasterTrailDebugLayer');
      return false;
    }

    const frozen = freezeTrail(routes);
    state.lockedMasterTrail = frozen;
    state.lockedBoundaries = frozen ? deriveBoundariesFromRoutes(routes, frozen) : {};
    // guidedDayCount doesn't self-derive once set (see PlanTrailRoutes.vue's
    // fallback-to-maxDay computed) — keep it in sync with the new actual count.
    state.guidedDayCount = Math.max(getters['maxDay'] as number, MIN_GUIDED_DAYS);

    await dispatch('updateMasterTrailDebugLayer');
    return true;
  },

  /**
   * Re-splits the FROZEN master trail into `rawDayCount` even days, replacing
   * every main-line route. Alt/spur legs keep their own geometry untouched
   * but are REGROUPED onto whichever new day they now sit closest to (their
   * start point's nearest position along the re-sliced trail) — the old day
   * count's boundaries no longer mean anything, so keeping the old day
   * number would frequently leave them listed under a day whose route no
   * longer runs anywhere near them. Transport legs (which can run between
   * two off-trail points, e.g. a taxi between two accommodations, with no
   * reliable on-trail anchor to project from) fall back to the simple
   * clamp-to-range behaviour instead. Resets every internal boundary to its
   * even-split position, discarding any manual drags/detours from before the
   * re-slice. Deactivates whatever day/route was active — the day list
   * should read as fully collapsed after a re-slice, not jump to (or stay
   * on) any particular day.
   */
  async applyDayCount({ state, getters, commit, dispatch }: PlanTrailActionContext, rawDayCount: number) {
    const dayCount = Math.min(MAX_GUIDED_DAYS, Math.max(MIN_GUIDED_DAYS, Math.round(rawDayCount)));
    const routes = getters['routesArray'] as Route[];

    if (!canDeriveMasterTrail(routes)) {
      console.warn('[GUIDED_MODE] Cannot apply day count — route is not a single continuous line');
      return;
    }

    // Use the frozen trail — never re-derive from current routes, which may
    // already contain a boundary detour baked in from a prior drag.
    if (!state.lockedMasterTrail) {
      if (state.pendingProvisionalReplacements.size > 0) {
        const settled = await waitForPendingRouting(state);
        if (!settled) {
          console.warn('[GUIDED_MODE] Cannot apply day count — still waiting for the last route calculation to finish');
          return;
        }
      }
      state.lockedMasterTrail = freezeTrail(getters['routesArray'] as Route[]);
    }
    if (!state.lockedMasterTrail) return;
    const frozen = state.lockedMasterTrail;

    const trail = {
      coordinates: frozen.coordinates,
      distanceMeters: frozen.cumDist[frozen.cumDist.length - 1] ?? 0,
      elevationGainMeters: 0,
    };
    if (trail.coordinates.length < 2) return;

    const slices = sliceMasterTrailIntoDays(trail, dayCount);
    if (slices.length === 0) return;

    const routesBefore = JSON.parse(JSON.stringify(state.routes));
    const activeRouteKeyBefore = state.activeRouteKey;
    const dayInfoBefore = JSON.parse(JSON.stringify(state.dayInfo));

    const nonMainRoutes = routes.filter(route => !isMainRouteStyle(route));
    const mainRouteIds = routes.filter(isMainRouteStyle).map(route => route.id);
    mainRouteIds.forEach(id => { delete state.routes[id]; });

    const newMainRoutes = slices.map(slice => {
      const route = RouteFactory.createNew(`Day ${slice.dayNumber}`, 0, slice.dayNumber);
      route.markers = buildBoundaryMarkers(slice.coordinates);
      route.lineStrings = [LineStringFeature.coordsOnlyFeature(slice.coordinates)];
      return route;
    });
    newMainRoutes.forEach(route => { state.routes[route.id] = route; });

    // Every internal boundary resets to its even-split position — any manual
    // drags/detours from before this re-slice no longer apply. Computed here
    // (before regrouping alt/spur legs below) so both can share it.
    const total = trail.distanceMeters;
    const evenBoundaries: { [dayNumber: number]: LockedBoundary } = {};
    for (let day = 1; day < dayCount; day++) {
      evenBoundaries[day] = { distanceM: (total * day) / dayCount, offTrailPoint: null, detourLine: null };
    }

    const newDayByRouteId = new Map<string, number>();
    nonMainRoutes.forEach(route => {
      let newDay: number;
      if (route.route_style === 'transport') {
        newDay = Math.min(Math.max(route.day ?? 1, 1), dayCount);
      } else {
        const anchor = routeAnchorCoord(route);
        newDay = anchor
          ? dayNumberForDistance(nearestOnTrail(trail.coordinates, frozen.cumDist, anchor[0], anchor[1]).distanceM, evenBoundaries, dayCount)
          : Math.min(Math.max(route.day ?? 1, 1), dayCount);
      }
      newDayByRouteId.set(route.id, newDay);
      if (state.routes[route.id]) {
        state.routes[route.id].day = newDay;
      }
    });

    const finalOrder: string[] = [];
    newMainRoutes.forEach(mainRoute => {
      finalOrder.push(mainRoute.id);
      nonMainRoutes
        .filter(route => newDayByRouteId.get(route.id) === mainRoute.day)
        .sort((a, b) => a.position - b.position)
        .forEach(route => finalOrder.push(route.id));
    });
    commit('updateRoutePositions', finalOrder);

    // A re-slice deactivates everything — no day is left active/expanded,
    // matching the "nothing active" state every other load/import path uses.
    state.activeRouteKey = null;
    state.guidedDayCount = dayCount;
    // Old day names/descriptions were tied to the previous boundaries — a
    // full re-slice makes them meaningless, so they're cleared rather than
    // left silently mismatched against the new days.
    //
    // The hike_day row IDs are deliberately NOT cleared with them. Server-side
    // a day is matched by day_number, so day N's row is still day N's row
    // after a re-slice. Dropping the IDs makes getHikeDaysAttributes send
    // hike_days_attributes with none at all, and HikeContentUpdateWorker's
    // `hike.hike_days.where.not(id: incoming_ids).destroy_all` then matches
    // EVERY row (`where.not(id: [])` compiles to `WHERE 1=1`) — destroying
    // each day's download_areas (the user's offline maps, `dependent:
    // :destroy`) and orphaning its print_map_pages.
    //
    // Days beyond the new count keep no entry: they genuinely no longer
    // exist, so leaving them out of the payload is the correct way to have
    // the server remove them.
    const reslicedDayInfo: PlanTrailState['dayInfo'] = {};
    for (let dayNumber = 1; dayNumber <= dayCount; dayNumber++) {
      const existingId = dayInfoBefore[dayNumber]?.id;
      if (existingId != null) {
        reslicedDayInfo[dayNumber] = { id: existingId, name: '', description: '' };
      }
    }
    state.dayInfo = reslicedDayInfo;

    state.lockedBoundaries = evenBoundaries;

    state._statsVersion++;

    state.undoManager.push(new ApplyDayCountCommand({
      routesBefore,
      routesAfter: JSON.parse(JSON.stringify(state.routes)),
      activeRouteKeyBefore,
      activeRouteKeyAfter: state.activeRouteKey,
      dayInfoBefore,
      dayInfoAfter: JSON.parse(JSON.stringify(reslicedDayInfo)),
    }));

    await dispatch('addAllRoutesToMap');
  },
};
